import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Users, Target, CheckCircle } from "lucide-react";
import Navigation from "@/components/Navigation";
import FooterWithNewsletter from "@/components/ui/footer-column";
import ClientsSection from "@/components/ClientsSection";
import PartnersSection from "@/components/PartnersSection";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { navigateToEnterpriseDemo } from "@/utils/navigation";


const AboutPage = () => {
  const navigate = useNavigate();

  const [_animatedNumbers, setAnimatedNumbers] = useState({
    multiplier: 0,
    time: 0,
    cost: 0,
    countries: 0
  });
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedNumbers({
        multiplier: 6,
        time: 5,
        cost: 70,
        countries: 48
      });
    }, 500);
    return () => clearTimeout(timer);
  }, []);
  const _testimonials = [{
    quote: "gimmefy cut our launch calendar from six weeks to six days.",
    author: "Nadia Wong, CMO, FinTech X",
    image: "testimonial1.jpg"
  }, {
    quote: "Margins up 40% because my micro-agency now works like a 20-person shop.",
    author: "Jordan Li, Creative Director, SparkForge",
    image: "testimonial2.jpg"
  }, {
    quote: "Thirty reels, one afternoon—followers grew 30% in a month.",
    author: "@TechTara, Content Creator",
    image: "testimonial3.jpg"
  }];
  const comparisonData = [{
    feature: "Built by senior marketers",
    gimmefy: true,
    generic: "Built by generic engineers"
  }, {
    feature: "Brand Vault locks tone & visuals",
    gimmefy: true,
    generic: "Manual brand prompts—hit or miss"
  }, {
    feature: "Studio, Agents, Playbooks, Visual Lab — one login",
    gimmefy: true,
    generic: "Patchwork of point apps"
  }, {
    feature: "AI updated weekly by in-house marketing R&D crew",
    gimmefy: true,
    generic: "Static model, stale best-practices"
  }];

  return <div className="min-h-screen bg-background">
    <Navigation />

    {/* Hero Section */}
    <section className="pt-32 pb-24 bg-gradient-hero-background relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-subtle opacity-30"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-8 animate-fade-in">
          <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-tight">
            When specialists design a specialist.
          </h1>

          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            We're lifelong marketers who built the platform we always wished existed: one chat window that turns sparks into scroll-stopping campaigns.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
            <div className="flex flex-col items-center">
              <Button
                variant="hero"
                size="lg"
                className="group text-xl px-12 py-8"
                onClick={() => window.location.href = '/signup/free'}
              >
                Start Free
                <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                No credit card required<br />
                monthly free credits
              </p>
            </div>
            <Button
              variant="outline-dark"
              size="lg"
              className="text-xl px-12 py-8"
              onClick={() => navigateToEnterpriseDemo(navigate)}
            >
              Book Enterprise Demo
            </Button>
          </div>
        </div>
      </div>
    </section>

    {/* Origin Story */}
    <section className="py-24 bg-gradient-accent">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              Built in agency war-rooms,
              <span className="bg-gradient-primary bg-clip-text text-transparent"> not ivory towers.</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              In 2022, agency veterans—brand strategists, content specialists, and media pros—set out to fix what AI tools missed: true marketing expertise. With decades of campaign experience, they knew content needed creative instinct as much as automation.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              That's why we built gimmefy: the marketing content specialist, fusing real marketer insight with the best in AI tech. The result? On-brand, campaign-ready assets—fast, consistent, and always crafted for results.
            </p>
          </div>
          <div className="lg:order-2">
            <div className="w-full h-96 rounded-2xl border border-border/50 overflow-hidden">
              <img
                src="/lovable-uploads/ec852f4f-399e-43c0-8878-4038e1e30cee.jpg"
                alt="Founder in modern office setting"
                className="w-full h-full object-cover"
                width={662}
                height={382}
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Mission & Vision */}
    <section className="py-24">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Our <span className="bg-gradient-primary bg-clip-text text-transparent">Purpose</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Driving innovation and democratizing marketing excellence for everyone
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 bg-gradient-card border-0 shadow-elegant hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center mx-auto">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <div className="space-y-4">
                  <Badge variant="secondary" className="bg-primary/10 text-primary text-sm font-medium">
                    Mission
                  </Badge>
                  <h3 className="text-2xl font-bold text-foreground">
                    Democratise Excellence
                  </h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Turn anyone with an idea into a launch-ready creator by democratising world-class marketing capabilities.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-gradient-card border-0 shadow-elegant hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center mx-auto">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <div className="space-y-4">
                  <Badge variant="secondary" className="bg-primary/10 text-primary text-sm font-medium">
                    Vision
                  </Badge>
                  <h3 className="text-2xl font-bold text-foreground">
                    Unleash Imagination
                  </h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    A world where brand brilliance isn't gated by budget, headcount, or tech know-how—only imagination.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>

    {/* Differentiator Table */}
    <section className="py-24 bg-gradient-accent">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-foreground">
            Why <span className="bg-gradient-primary bg-clip-text text-transparent">gimmefy</span> wins
          </h2>

          <div className="overflow-x-auto">
            <div className="min-w-full bg-card rounded-2xl border border-border shadow-elegant">
              <div className="grid grid-cols-3 gap-0">
                {/* Header */}
                <div className="p-6 border-b border-border"></div>
                <div className="p-6 border-b border-border border-l text-center">
                  <h3 className="font-bold text-primary">gimmefy</h3>
                </div>
                <div className="p-6 border-b border-border border-l text-center">
                  <h3 className="font-bold text-muted-foreground">Generic AI Tool</h3>
                </div>

                {/* Rows */}
                {comparisonData.map((row, index) => <>
                  <div key={`feature-${index}`} className="p-6 border-b border-border font-medium text-foreground">
                    {row.feature}
                  </div>
                  <div className="p-6 border-b border-border border-l text-center">
                    <CheckCircle className="h-6 w-6 text-primary mx-auto" />
                  </div>
                  <div className="p-6 border-b border-border border-l  text-center text-muted-foreground">
                    {typeof row.generic === 'string' ? row.generic : '✗'}
                  </div>
                </>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Global Clients */}

    {/* Clients Testimonials Section */}
    <ClientsSection />

    {/* Partners Section */}
    <PartnersSection />

    {/* Security & Compliance */}

    {/* Careers Callout */}

    {/* Final CTA */}
    <section className="py-32 bg-gradient-hero-background relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-subtle opacity-30"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-5xl md:text-7xl font-bold text-foreground leading-tight">
            Ready to gimmefy your marketing content?
          </h2>

          <p className="text-xl text-muted-foreground">
            Start free today or book an enterprise walkthrough—your brand, only faster.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
            <div className="flex flex-col items-center">
              <Button
                variant="hero"
                size="lg"
                className="group text-xl px-12 py-8"
                onClick={() => window.location.href = '/signup/free'}
              >
                Start Free
                <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                No credit card required<br />
                monthly free credits
              </p>
            </div>
            <Button
              variant="outline-dark"
              size="lg"
              className="text-xl px-12 py-8"
              onClick={() => navigateToEnterpriseDemo(navigate)}
            >
              Book Demo
            </Button>
          </div>
        </div>
      </div>
    </section>

    <FooterWithNewsletter />
  </div>;
};

export default AboutPage;
